import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import TechnikaServiceApi from "./TechnikaService";
import type { Technika } from "../generated/models/Technika";
import type {
    DostupnostTechnika,
    TechnikaDostupnostPrehled,
    SetDostupnostTechnikaRequest,
} from "../generated/models/DostupnostTechnika";
import type { TechnikaRequest } from "../generated/models/TechnikaRequest";
import type { TechnikaKontrola, CreateTechnikaKontrolaRequest, TechnikaKontrolyOverviewResponse } from "../generated/models/TechnikaKontrola";
import type { JizdaResponse } from "../generated/models/Jizda";
import type { KontrolaStatusResponse } from "../generated/models/Kontrola";

// QUERIES

export const useAllTechnikaQuery = (opts?: { enabled?: boolean }) => {
    return useQuery<Technika[], Error>({
        queryKey: ["technika"],
        queryFn: () => TechnikaServiceApi.getAllTechnika(),
        staleTime: 5 * 60 * 1000,
        enabled: opts?.enabled ?? true,
    });
};

export const useActiveTechnikaQuery = () => {
    return useQuery<Technika[], Error>({
        queryKey: ["technika", "active"],
        queryFn: () => TechnikaServiceApi.getActiveTechnika(),
        staleTime: 5 * 60 * 1000,
    });
};

// CREATE MUTATION

export const useCreateTechnikaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Technika, Error, TechnikaRequest>({
        mutationFn: (data) => TechnikaServiceApi.createTechnika(data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["technika"] });
            showToast("Technika byla úspěšně vytvořena.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit techniku.", "error");
        },
    });
};

// UPDATE MUTATION

export const useUpdateTechnikaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Technika, Error, { id: number; data: TechnikaRequest }>({
        mutationFn: ({ id, data }) => TechnikaServiceApi.updateTechnika(id, data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["technika"] });
            showToast("Technika byla úspěšně aktualizována.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat techniku.", "error");
        },
    });
};

// DELETE MUTATION

export const useDeleteTechnikaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => TechnikaServiceApi.deleteTechnika(id),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["technika"] });
            showToast("Technika byla úspěšně smazána.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat techniku.", "error");
        },
    });
};

// FOTO MUTATIONS

export const useUploadFotografieMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean; message: string }, Error, { id: number; file: File }>({
        mutationFn: ({ id, file }) => TechnikaServiceApi.uploadFotografie(id, file),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["technika"] });
            showToast("Fotografie byla úspěšně nahrána.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se nahrát fotografii.", "error");
        },
    });
};

export const useDeleteFotografieMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean; message: string }, Error, number>({
        mutationFn: (id) => TechnikaServiceApi.deleteFotografie(id),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["technika"] });
            showToast("Fotografie byla úspěšně smazána.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat fotografii.", "error");
        },
    });
};

// ==========================================
// TECHNIKA DETAIL QUERIES
// ==========================================

export const useTechnikaByIdQuery = (id: number) => {
    return useQuery<Technika, Error>({
        queryKey: ["technika", id],
        queryFn: () => TechnikaServiceApi.getTechnikaById(id),
        enabled: !!id,
    });
};

export const useKnihaJizdQuery = (technikaId: number | null) => {
    return useQuery<JizdaResponse[], Error>({
        queryKey: ["technikaJizdy", technikaId],
        queryFn: () => TechnikaServiceApi.getKnihaJizd(technikaId!),
        enabled: !!technikaId,
    });
};

export const useTechnikaKontrolyOverviewQuery = () => {
    return useQuery<TechnikaKontrolyOverviewResponse, Error>({
        queryKey: ["technikaKontrolyOverview"],
        queryFn: () => TechnikaServiceApi.getKontrolyOverview(),
    });
};

export const useKontrolyForTechnikaQuery = (technikaId: number | null) => {
    return useQuery<KontrolaStatusResponse[], Error>({
        queryKey: ["technikaKontroly", technikaId],
        queryFn: () => TechnikaServiceApi.getKontrolyForTechnika(technikaId!),
        enabled: !!technikaId,
    });
};

export const useTechnikaKontrolyHistorieQuery = (technikaId: number | null, typId: number | null) => {
    return useQuery<TechnikaKontrola[], Error>({
        queryKey: ["technikaKontrolyHistorie", technikaId, typId],
        queryFn: () => TechnikaServiceApi.getKontrolyHistorie(technikaId!, typId!),
        enabled: !!technikaId && !!typId,
    });
};

// ==========================================
// TECHNIKA KONTROLA MUTATIONS
// ==========================================

export const useCreateTechnikaKontrolaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<TechnikaKontrola, Error, { technikaId: number; data: CreateTechnikaKontrolaRequest }>({
        mutationFn: ({ technikaId, data }) => TechnikaServiceApi.createTechnikaKontrola(technikaId, data),

        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["technikaKontroly", variables.technikaId] });
            queryClient.invalidateQueries({ queryKey: ["technikaKontrolyHistorie"] });
            queryClient.invalidateQueries({ queryKey: ["technika"] });
            queryClient.invalidateQueries({ queryKey: ["technikaKontrolyOverview"] });
            showToast("Kontrola byla zaznamenána.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se zaznamenat kontrolu.", "error");
        },
    });
};

export const useUpdateTechnikaKontrolaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<TechnikaKontrola, Error, { kontrolaId: number; technikaId: number; data: CreateTechnikaKontrolaRequest }>({
        mutationFn: ({ kontrolaId, data }) => TechnikaServiceApi.updateTechnikaKontrola(kontrolaId, data),

        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["technikaKontroly", variables.technikaId] });
            queryClient.invalidateQueries({ queryKey: ["technikaKontrolyHistorie"] });
            queryClient.invalidateQueries({ queryKey: ["technika"] });
            queryClient.invalidateQueries({ queryKey: ["technikaKontrolyOverview"] });
            showToast("Kontrola byla aktualizována.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat kontrolu.", "error");
        },
    });
};

export const useDeleteTechnikaKontrolaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { kontrolaId: number; technikaId: number }>({
        mutationFn: ({ kontrolaId }) => TechnikaServiceApi.deleteTechnikaKontrola(kontrolaId),

        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["technikaKontroly", variables.technikaId] });
            queryClient.invalidateQueries({ queryKey: ["technikaKontrolyHistorie"] });
            queryClient.invalidateQueries({ queryKey: ["technika"] });
            queryClient.invalidateQueries({ queryKey: ["technikaKontrolyOverview"] });
            showToast("Záznam kontroly byl smazán.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat záznam kontroly.", "error");
        },
    });
};

// ==========================================
// HO-3 DOSTUPNOST TECHNIKY
// ==========================================

export const useTechnikaDostupnostPrehledQuery = () => {
    return useQuery<TechnikaDostupnostPrehled[], Error>({
        queryKey: ['technika-dostupnost'],
        queryFn: () => TechnikaServiceApi.getDostupnostPrehled(),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
    });
};

export const useOdhlasitTechnikuMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<DostupnostTechnika, Error, { id: number; data: SetDostupnostTechnikaRequest }>({
        mutationFn: ({ id, data }) => TechnikaServiceApi.odhlasitTechniku(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['technika-dostupnost'] });
            queryClient.invalidateQueries({ queryKey: ['technika'] });
            showToast("Vozidlo bylo odhlášeno.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se odhlásit vozidlo.", "error");
        },
    });
};

export const useUkoncitDostupnostMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<unknown, Error, number>({
        mutationFn: (dostupnostId) => TechnikaServiceApi.ukoncitDostupnost(dostupnostId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['technika-dostupnost'] });
            queryClient.invalidateQueries({ queryKey: ['technika'] });
            showToast("Vozidlo je zpět v provozu.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se přihlásit vozidlo.", "error");
        },
    });
};
